import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { GridWrapper } from '$partials/grid/layout/index.js';
import { ProjectCategoryLinks } from '$partials/global/index.js';
import GridModuleDispatcher from '$partials/grid/dispatcher.jsx';

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * Track scroll progress through an element
 * @param {object} options
 * @returns {array}
 */
const useProgress = ({
    watch = false
}) => {
    const [progress, setProgress] = useState(0);
    const ref = useRef();

    let didScroll = false;

    const measureProgress = () => {
        if (ref.current) {
            const r = ref.current.getBoundingClientRect();
            setProgress(Math.min(1, Math.max(0, -r.top / r.height)));
        }
    };

    const handleScroll = () => {
        if (!didScroll) {
            window.requestAnimationFrame(() => {
                if (watch) { measureProgress(); }
                didScroll = false;
            });
            didScroll = true;
        }
    };

    useEffect(() => {
        if (watch) {
            measureProgress();
            window.addEventListener('scroll', handleScroll);
        } else {
            window.removeEventListener('scroll', handleScroll);
        }

        return () => window.removeEventListener('scroll', handleScroll);
    }, [watch]);

    return [ref, { progress }];
};

/**
 * Measure element
 * @param {object} options
 * @returns {array}
 */
const useDimensions = ({
    watch = false
}) => {
    const [width, setWidth] = useState();
    const [height, setHeight] = useState();
    const ref = useRef();

    let didResize = false;

    const measure = () => {
        if (ref.current) {
            const r = ref.current.getBoundingClientRect();
            setWidth(r.width);
            setHeight(r.height);
        }
    };

    const handleResize = () => {
        if (!didResize) {
            window.requestAnimationFrame(() => {
                measure();
                didResize = false;
            });
            didResize = true;
        }
    };

    useEffect(() => {
        if (watch) {
            handleResize();
            window.addEventListener('resize', handleResize);
        } else {
            window.removeEventListener('resize', handleResize);
        }

        return () => window.removeEventListener('resize', handleResize);
    }, [watch]);

    return [ref, { width, height }];
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * An individual project
 * @param {PropTypes.InferProps<typeof projectPropTypes>} props
 */
const Project = React.forwardRef(({
    backgroundColour,
    categories,
    fixedPosition = false,
    foregroundColour,
    grid,
    id,
    isCurrentProject = false,
    isFirst = false,
    isVisible = false,
    onProgressChange = () => { },
    projectCopy,
    projectDisplayTitle,
}, ref) => {
    const [opacityMin, setOpacityMin] = useState(0);
    const [progressRef, { progress }] = useProgress({ watch: isVisible });
    const [projectRef, { height }] = useDimensions({ watch: fixedPosition ? 2 : 1 });
    const refCallback = useCallback((node) => {
        progressRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else {
            ref.current = node;
        }
    }, []);

    useEffect(() => {
        (typeof onProgressChange === 'function') && onProgressChange(progress);
    }, [progress]);

    useEffect(()=> {
        if (fixedPosition) {
            setOpacityMin(progress);
        }
    }, [fixedPosition]);

    const remapRange = (value, inMin, inMax, outMin, outMax) => {
        if (typeof value !== 'number') {
            return 0;
        }
        return (value - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
    };
    const HeadingComponent = isCurrentProject ? 'h1' : 'h2';

    const className = encodeURIComponent(`project-page-${id}`)
        .toLowerCase()
        .replace(/\.|%[0-9a-z]{2}/gi, '');

    const tags = categories?.edges?.length > 0 ? [...categories.edges.map((e) => e.node)]
        .sort((a, b) => b?.node?.projects?.total - a?.node?.projects?.total) : [];

    return (
        <div ref={refCallback}>
            {(backgroundColour && foregroundColour) &&
                <style dangerouslySetInnerHTML={{
                    __html: `
                    .${className} {
                        --background: ${backgroundColour};
                        --foreground: ${foregroundColour};
                    }
                `}} />
            }

            <div
                className='z-10 relative transition-opacity'
                style={{
                    opacity: fixedPosition ? remapRange(progress, opacityMin, 1, 0, 1) : 0
                }}>
                {fixedPosition && (
                    <div
                        className='bg-midnight h-full'
                        style={{
                            height: `${height}px`,
                        }}/>
                )}
            </div>

            <div
                className={`${className} ${fixedPosition ? 'fixed bottom-0 w-full' : 'relative z1'} bg-background px-1`}
                ref={projectRef}>
                <div className='rounded mb-[1px] layout-container pt-20 sm:pt-40 pb-28 sm:pb-40 md:pb-40 lg:pb-55 bg-foreground'>
                    <header className='px-layout-item flex flex-col gap-27'>
                        {isFirst &&
                            <a
                                className='flex flex-col type-style-2 mb-10 sm:mb-30'
                                href={'/'}>
                                <span>Bureau for</span>
                                <span>Visual Affairs</span>
                            </a>
                        }

                        <div className='type-style-hero-sm'>
                            <HeadingComponent dangerouslySetInnerHTML={{ __html: projectDisplayTitle }} />
                        </div>

                        {projectCopy &&
                            <div
                                className='type-style-4 max-w-[28em] sm:max-w-[56em] sm:columns-2 sm:gap-30 md:gap-40 lg:gap-50 rich-text mb-30 sm:mb-0'
                                dangerouslySetInnerHTML={{ __html: projectCopy }} />
                        }

                        {tags.length > 0 &&
                            <div className='-mx-layout-item width-full overflow-x-auto flex gap-10 hide-scrollbar snap-x'>
                                <ProjectCategoryLinks
                                    categories={tags}
                                    className='snap-start first:pl-layout-item last:pr-layout-item' />
                            </div>
                        }
                    </header>
                </div>

                {/* Outer grid */}
                <GridWrapper>
                    {/* Modules */}
                    {grid?.map((item) => {
                        if (!item) { return null; }

                        return (
                            <GridModuleDispatcher
                                {...item}
                                key={item.id} />
                        );
                    })}

                    {/* Final spacer - one cell high */}
                    <div className='col-span-3 sm:col-span-5 md:col-span-7 lg:col-span-9 aspect-[3/1] sm:aspect-[5/1] md:aspect-[7/1] lg:aspect-[9/1] w-full pointer-events-none' />
                </GridWrapper>
            </div>
        </div>
    );
});

Project.displayName = 'Project';

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    backgroundColour: PropTypes.string,
    categories: PropTypes.object,
    fixedPosition: PropTypes.bool,
    foregroundColour: PropTypes.string,
    grid: PropTypes.array,
    id: PropTypes.string,
    isCurrentProject: PropTypes.bool,
    isFirst: PropTypes.bool,
    isVisible: PropTypes.bool.isRequired,
    onProgressChange: PropTypes.func,
    projectCopy: PropTypes.string,
    projectDisplayTitle: PropTypes.string,
    title: PropTypes.string
};

Project.propTypes = propTypes;

export default Project;
